import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import { useDebounce } from 'helpers'
import useIsMobile from 'hooks/useIsMobile'
import { useState, useCallback, useMemo } from 'react'
import { ChevronDown } from 'react-feather'
import { Card, CardBody, Spinner } from 'reactstrap'
import { useFetchMapSchemasQuery } from '../map-schema/mapSchemaApiSlice'
import SvgIcon from 'components/Icon/SvgIcon'
import ModalCalculate from './ModalCalculate'
import { useFetchProjectSummaryDashboardQuery } from '../project-dashboard/projectDashboardListApiSlice'

// ── Per-goal schema table ──
const SchemaTablePerGoal = ({ goal, dataProject, isMobile, inventoryMap }) => {
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)
  const [sortby, setSortby] = useState('createdAt')
  const [sortdir, setSortdir] = useState('DESC')
  const [searchInput, setSearchInput] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [isFilter, setIsFilter] = useState(false)
  const [selectedSchema, setSelectedSchema] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const toggleModal = useCallback(() => setIsModalOpen((prev) => !prev), [])

  const { data, isFetching } = useFetchMapSchemasQuery(
    {
      itemId: goal?.itemId,
      page,
      size,
      s: searchValue || undefined,
      sortby,
      sortdir,
    },
    { refetchOnMountOrArgChange: true, skip: !goal?.itemId },
  )

  const debouncedSearch = useDebounce((value) => {
    setPage(0)
    setSearchValue(value)
  }, 600)

  const onHandleSearchChange = (value) => {
    if (!value?.trim()) {
      setIsFilter(false)
    } else {
      setIsFilter(true)
    }
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && searchValue?.length === 0) {
      e.preventDefault()
    }
  }

  const handleSort = (column, direction) => {
    setSortby(column.sortField)
    setSortdir(direction?.toUpperCase())
  }

  const columns = [
    {
      name: <span>Nama Skema</span>,
      selector: 'createMethodLabel',
      // sortable: true,
      sortField: 'createMethodLabel',
      grow: 2,
      cell: (row) => <span className="m-0">{row?.createMethodLabel}</span>,
      minWidth: '200px',
    },
    {
      name: <span>Jumlah Bahan Baku</span>,
      selector: 'numInput',
      // sortable: true,
      sortField: 'numInput',
      grow: 1,
      cell: (row) => (
        <span className="m-0 text-center d-block">{row?.numInput}</span>
      ),
      minWidth: '120px',
      center: true,
    },
    {
      name: <span>Jumlah Barang Jadi</span>,
      selector: 'numOutput',
      // sortable: true,
      sortField: 'numOutput',
      grow: 1,
      cell: (row) => (
        <span className="m-0 text-center d-block">{row?.numOutput}</span>
      ),
      minWidth: '120px',
      center: true,
    },
    {
      name: <span className="fw-semibold"></span>,
      width: '120px',
      right: true,
      cell: (row, index) => (
        <div
          className="d-flex align-items-center justify-content-end"
          style={{ gap: '12px' }}
          onClick={() => {
            setSelectedSchema(row)
            toggleModal()
          }}
        >
          <div id={`edit-schema-${goal.itemId}-${index}`}>
            <SvgIcon
              name="calculator"
              size={30}
              id="transaksi"
              className="cursor-pointer"
              style={{ cursor: 'pointer' }}
            />
          </div>
          <PMBTooltip
            target={`edit-schema-${goal.itemId}-${index}`}
            text="Hitung"
          />
        </div>
      ),
    },
  ]

  return (
    <>
      <ModalCalculate
        data={selectedSchema}
        dataProject={dataProject}
        inventoryMap={inventoryMap}
        isOpen={isModalOpen}
        toggle={toggleModal}
      />
      <Card className="border-0 shadow-sm mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Skema {goal?.nameItem}</h5>
          </div>
          <DataTable
            responsive
            persistTableHead
            defaultSortFieldId={1}
            defaultSortAsc={true}
            columns={columns}
            data={data?.items || []}
            progressPending={isFetching}
            paginationTotalRows={data?.totalItems}
            pagination
            paginationServer
            highlightOnHover
            pointerOnHover
            customStyles={{
              rows: {
                style: {
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                  },
                },
              },
            }}
            paginationComponent={() => (
              <PMBPagination
                rowCount={data?.totalItems || 0}
                currentPage={page}
                rowsPerPage={size}
                setCurrentPage={(e) => setPage(e)}
                setRowsPerPage={(e) => {
                  setSize(e)
                  setPage(0)
                }}
              />
            )}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner color="primary" />
              </div>
            }
            onSort={handleSort}
            sortIcon={<ChevronDown />}
            sortServer
            // subHeader={true}
            // subHeaderComponent={
            //   <div className="w-100">
            //     <div className="d-flex justify-content-between align-items-center mb-1">
            //       <div className="mb-2">
            //         <div
            //           className={
            //             isMobile
            //               ? 'd-flex flex-column gap-3'
            //               : 'd-flex justify-content-between align-items-end'
            //           }
            //         >
            //           <div style={{ width: isMobile ? '100%' : 'auto' }}>
            //             <PMBSearchInputGroup
            //               value={searchInput}
            //               placeholder="Cari nama skema..."
            //               onChange={(e) => onHandleSearchChange(e.target.value)}
            //               maxWidth={isMobile ? '100%' : '350px'}
            //               onKeyDown={handleKeyDown}
            //             />
            //           </div>
            //         </div>
            //       </div>
            //     </div>
            //   </div>
            // }
            noDataComponent={
              <p className="my-3 fw-semibold">
                {isFilter
                  ? 'Tidak ada data yang sesuai dengan pencarian Anda'
                  : 'Belum ada skema yang tersedia'}
              </p>
            }
          />
        </CardBody>
      </Card>
    </>
  )
}

// ── Main component: renders one table per goal ──
const CalculatorSchema = ({ projectId, data: dataProject }) => {
  const isMobile = useIsMobile()

  const { data: summaryData } = useFetchProjectSummaryDashboardQuery(
    { projectId },
    { skip: !projectId },
  )

  const inventoryMap = useMemo(() => {
    const map = {}
    summaryData?.items?.forEach((item) => {
      if (!item?.itemId) return
      const totalInventory = Number(item?.totalInventory) || 0
      map[item.itemId] = totalInventory
    })
    return map
  }, [summaryData?.items])

  const goals = dataProject?.goals || []

  if (!goals?.length) {
    return (
      <Card className="border-0 shadow-sm">
        <CardBody>
          <p className="my-3 fw-semibold text-center">
            Belum ada target yang tersedia untuk menampilkan skema
          </p>
        </CardBody>
      </Card>
    )
  }

  return (
    <div className="d-flex flex-column gap-3">
      {goals?.map((goal) => (
        <SchemaTablePerGoal
          key={goal.itemId}
          goal={goal}
          dataProject={dataProject}
          isMobile={isMobile}
          inventoryMap={inventoryMap}
        />
      ))}
    </div>
  )
}

export default CalculatorSchema
